import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface ThumbPickerItem {
  id: string
  src: string
}

export interface ThumbPickerProps extends Omit<BoxProps, 'onChange'> {
  items: ThumbPickerItem[]
  value?: string
  onChange: (id: string) => void
}

const Row = styled(Box)({
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
})

const Thumb = styled('button', {
  shouldForwardProp: (p) => p !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: 38,
  height: 38,
  borderRadius: 8,
  overflow: 'hidden',
  cursor: 'pointer',
  outline: $selected ? '2px solid var(--ds-ink)' : '2px solid transparent',
  outlineOffset: 2,
  transition: 'outline-color var(--ds-duration-fast) var(--ds-ease)',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  '&:focus-visible': {
    outline: '2px solid var(--ds-ink)',
  },
}))

/** Compact cover/thumbnail picker — 38px selectable thumbs with ink outline. */
export function ThumbPicker({ items, value, onChange, ...rest }: ThumbPickerProps) {
  if (items.length === 0) return null
  return (
    <Row role='listbox' aria-label='Choose cover' {...rest}>
      {items.map((item) => (
        <Thumb
          key={item.id}
          type='button'
          role='option'
          aria-selected={item.id === value}
          $selected={item.id === value}
          onClick={() => onChange(item.id)}
        >
          <img src={item.src} alt='' loading='lazy' />
        </Thumb>
      ))}
    </Row>
  )
}
